import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { getAllSettingsService, updateAdminSetting } from '../services/Settings.service';

const KEY = 'order-emails-copy-to';
const TITLE = 'Order emails copy to';

/**
 * Internal copy of paid-order emails: this address is BCC'd on every order/subscription/credits
 * confirmation email (which only go out once payment succeeded).
 */
const OrderNotificationsSettings: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const settings = await getAllSettingsService();
        const current = settings.find((s: any) => s.id === KEY);
        setEmail(String(current?.settingValue || ''));
      } catch {
        // fields stay usable with defaults
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    // Empty = turn the internal copy off (the server accepts it for this key).
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error('Enter a valid email address, or clear the field to turn the copy off');
      return;
    }
    setSaving(true);
    try {
      await updateAdminSetting(KEY, TITLE, email.trim());
      toast.success(email.trim() ? `${TITLE} saved` : 'Internal order-email copy turned off');
    } catch (e: any) {
      toast.error(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h2 className="text-lg font-semibold mb-1">Order Notifications</h2>
      <p className="text-sm text-gray-500 mb-4">
        This address receives a copy (BCC) of every paid-order confirmation email — book orders,
        subscriptions, and image credits. Confirmations only go out after successful payment.
      </p>
      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="orders@yourcompany.com"
            className="border border-gray-300 rounded px-2 py-1.5 text-sm w-80 max-w-full"
          />
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-storyblue text-white rounded px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
};

export default OrderNotificationsSettings;
